import {
  type ApiContext,
  type ApiNotAuthorized,
  CatalogChanged,
  type InvalidFormField,
  type ScanCandidateUnknown,
  type ScanNotFound,
  type ScanNotReviewable,
  type ScanReviewGroup,
  type StoreNotFound,
} from "@hobbyshopify/backend-client";
import { type AsyncResult, ok } from "operation-result.js";
import type { AuthHandler } from "trunk.js";
import { errorText } from "../primitives/errors";
import { READ_FAILED_ON_THE_WAY, type ReviewEntry } from "../primitives/review-row";
import { shopEntryId } from "../primitives/workspace";
import type { ReviewQueueRepository } from "../repositories/review-queue-repository";
import type { StoreRepository } from "../repositories/store-repository";
import type { BackendApi } from "./api";

type ConfirmErrors =
  | ApiNotAuthorized
  | StoreNotFound
  | ScanNotFound
  | ScanNotReviewable
  | CatalogChanged
  | InvalidFormField;

/**
 * Fetches the queue, and the shop's scans no card was recognised in, into the
 * repository. Single flight, so a read that throws (a 5xx, offline) still
 * leaves it failed rather than loading for good, which would refuse every
 * later read. The throw goes on, as unexpected.
 */
export async function loadReviewQueue(o: {
  client: BackendApi;
  authHandler: AuthHandler<ApiContext>;
  shopId: string;
  locationId: string | null;
  reviewQueueRepository: ReviewQueueRepository;
}): Promise<void> {
  if (o.reviewQueueRepository.status.kind === "loading") return;
  o.reviewQueueRepository.setLoading();
  const r = await o.authHandler
    .callApi((ctx) =>
      o.client.scans.reviewQueue(ctx, shopEntryId(o.shopId, o.locationId), undefined, undefined, {
        enableNoMatch: true,
      }),
    )
    .catch((thrown: unknown) => {
      o.reviewQueueRepository.setError(READ_FAILED_ON_THE_WAY);
      throw thrown;
    });
  if (!r.ok) {
    o.reviewQueueRepository.setError(errorText(r.errors));
    return;
  }
  const scans = await o.authHandler
    .callApi((ctx) => o.client.scans.list(ctx, shopEntryId(o.shopId, o.locationId), { enableNoMatch: true }))
    .catch((thrown: unknown) => {
      o.reviewQueueRepository.setError(READ_FAILED_ON_THE_WAY);
      throw thrown;
    });
  if (!scans.ok) {
    o.reviewQueueRepository.setError("Could not load unrecognised scans.");
    return;
  }
  o.reviewQueueRepository.setGroups(
    r.value,
    scans.value.filter((scan) => scan.kind === "OWNER" && scan.recognitionOutcome === "no_match"),
  );
  if (
    scans.value.some(
      (scan) =>
        scan.kind === "OWNER" &&
        !scan.cardInfo &&
        scan.scanProgress !== "scan.completed" &&
        scan.scanProgress !== "scan.error" &&
        scan.review === "PENDING",
    )
  ) {
    setTimeout(() => o.reviewQueueRepository.markStale(), 2000);
  }
}

/**
 * Reads the queue again and resolves once a read sent after this call has answered (or failed): one already on its
 * way may have left before a change it should show, so it is waited for and the queue read after it. It stops once
 * another counter is chosen: the one queue is that counter's to read then.
 */
export async function readReviewQueueAgain(
  o: Parameters<typeof loadReviewQueue>[0] & { storeRepository: Pick<StoreRepository, "shopId" | "locationId"> },
): Promise<void> {
  const repo = o.reviewQueueRepository;
  repo.markStale();
  for (;;) {
    if (o.storeRepository.shopId !== o.shopId || o.storeRepository.locationId !== o.locationId) return;
    if (repo.status.kind === "loading") await nextChange(repo);
    else if (repo.isStale) await loadReviewQueue(o).catch(() => undefined);
    else return;
  }
}

const nextChange = (repo: ReviewQueueRepository) =>
  new Promise<void>((resolve) => {
    const changed = () => {
      repo.removeListener(changed);
      resolve();
    };
    repo.addListener(changed);
  });

/**
 * Confirms a whole group as one card and, unless the row says otherwise, one
 * lot of N, which is why this is confirm-many and not a loop. The queue is
 * marked stale on success; the drafts controller reloads it.
 */
export async function confirmGroup(o: {
  client: BackendApi;
  authHandler: AuthHandler<ApiContext>;
  shopId: string;
  entry: ReviewEntry;
  reviewQueueRepository: ReviewQueueRepository;
}): AsyncResult<void, ConfirmErrors> {
  const r = await readAgainIfThrown(o, () => confirmOne(o, o.entry));
  // The backend re-read the catalogue onto the group; the row shows it again.
  if (r.ok || r.errors.some((e) => e instanceof CatalogChanged)) o.reviewQueueRepository.markStale();
  return r.ok ? ok() : r;
}

/**
 * "Confirm all": sequential on purpose. Each confirm is a transaction that may
 * create the same card as the one before it, and the identity key is what
 * serialises them; racing ten of those just trades a queue for a retry loop.
 * Stops at the first refusal, and the queue is marked stale whenever at least
 * one group went through, so the page shows what did. `onConfirmed` hears of
 * each entry the backend took, as it goes.
 */
export async function confirmGroups(o: {
  client: BackendApi;
  authHandler: AuthHandler<ApiContext>;
  shopId: string;
  entries: readonly ReviewEntry[];
  reviewQueueRepository: ReviewQueueRepository;
  onConfirmed?: (entry: ReviewEntry) => void;
}): AsyncResult<void, ConfirmErrors> {
  let confirmed = false;
  for (const entry of o.entries) {
    const r = await readAgainIfThrown(o, () => confirmOne(o, entry));
    if (!r.ok) {
      if (confirmed || r.errors.some((e) => e instanceof CatalogChanged)) o.reviewQueueRepository.markStale();
      return r;
    }
    confirmed = true;
    o.onConfirmed?.(entry);
  }
  if (confirmed) o.reviewQueueRepository.markStale();
  return ok();
}

/**
 * A call that throws (a 5xx, a dropped connection) may still have gone through,
 * so the queue is read again; the throw goes on.
 */
async function readAgainIfThrown<T>(
  o: { reviewQueueRepository: ReviewQueueRepository },
  call: () => Promise<T>,
): Promise<T> {
  try {
    return await call();
  } catch (thrown) {
    o.reviewQueueRepository.markStale();
    throw thrown;
  }
}

/**
 * Sends a price only when it changes the card's, since each one is a price
 * history row, and never a floor: the backend saves the floor as given, so
 * sending one would overwrite the shop's own on every copy confirmed.
 */
function confirmOne(
  o: { client: BackendApi; authHandler: AuthHandler<ApiContext>; shopId: string },
  { group, priceCents, quantity, catalogApproval }: ReviewEntry,
) {
  return o.authHandler.callApi((ctx) =>
    o.client.scans.confirmMany(ctx, o.shopId, group.scanIds, {
      ...(priceCents !== null ? { priceCents } : {}),
      ...(quantity ? { stock: { quantity } } : {}),
      ...(catalogApproval ? { catalogApproval } : {}),
    }),
  );
}

/** Prices every scan of the group from the chosen candidate: the group is one card, so its scans move together. */
export async function pickGroupCandidate(o: {
  client: BackendApi;
  authHandler: AuthHandler<ApiContext>;
  shopId: string;
  group: ScanReviewGroup;
  productId: string;
  reviewQueueRepository: ReviewQueueRepository;
}): AsyncResult<void, ApiNotAuthorized | StoreNotFound | ScanNotFound | ScanNotReviewable | ScanCandidateUnknown> {
  for (const scanId of o.group.scanIds) {
    const r = await readAgainIfThrown(o, () =>
      o.authHandler.callApi((ctx) => o.client.scans.pick(ctx, o.shopId, scanId, o.productId)),
    );
    if (!r.ok) {
      o.reviewQueueRepository.markStale();
      return r;
    }
  }
  o.reviewQueueRepository.markStale();
  return ok();
}

/**
 * Rejects every scan of a group. One refusal fails the group: a half-rejected
 * group would leave the queue showing scans the reviewer believes they dismissed.
 * Each reject is its own authenticated call, so a retry never replays one that went through.
 */
export async function rejectGroup(o: {
  client: BackendApi;
  authHandler: AuthHandler<ApiContext>;
  shopId: string;
  group: ScanReviewGroup;
  reviewQueueRepository: ReviewQueueRepository;
}): AsyncResult<void, ApiNotAuthorized | StoreNotFound | ScanNotFound> {
  const results = await readAgainIfThrown(o, async () => {
    // Every reject answers before a throw goes on, so the read after it sees them all.
    const settled = await Promise.allSettled(
      o.group.scanIds.map((id) => o.authHandler.callApi((ctx) => o.client.scans.reject(ctx, o.shopId, id))),
    );
    const thrown = settled.find((s): s is PromiseRejectedResult => s.status === "rejected");
    if (thrown) throw thrown.reason;
    return settled.flatMap((s) => (s.status === "fulfilled" ? [s.value] : []));
  });
  const failed = results.find((r) => !r.ok);
  if (failed) return failed;
  o.reviewQueueRepository.markStale();
  return ok();
}
